"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from "react";
import { useGame } from "./GameContextProvider";
import { useGameMap } from "./GameMapContextProvider";
import { gameToLeafletCoordinates } from "@/lib/utils/coordinateConversion";

interface GuessContext {
    localGuessPosition: { lat: number, lng: number } | null;
    setLocalGuessPosition: (position: { lat: number, lng: number } | null) => void;
    placeGuess: (position: { lat: number, lng: number }) => void;
    confirmGuess: () => void;
    hasSubmittedGuess: boolean;
    hasTentativeGuess: boolean;
    guessMarkerPosition: { lat: number, lng: number } | null;
}

const GuessContext = createContext<GuessContext>({
    localGuessPosition: null,
    setLocalGuessPosition: () => { },
    placeGuess: () => { },
    confirmGuess: () => { },
    hasSubmittedGuess: false,
    hasTentativeGuess: false,
    guessMarkerPosition: null,
});

interface GuessProviderProps {
    children: ReactNode;
    disabled?: boolean;
}

export function GuessProvider({ children, disabled = false }: GuessProviderProps) {
    const { room, gameState } = useGame();
    const { currentMap } = useGameMap();
    const [localGuessPosition, setLocalGuessPositionState] = useState<{ lat: number, lng: number } | null>(null);

    // Get current player and their guess for this round from game state
    const currentPlayer = gameState?.players?.get(room?.sessionId || "");
    const currentRound = gameState?.currentRound?.toString() || "";
    const submittedGuess = currentPlayer?.guesses.get(currentRound);
    const hasSubmittedGuess = currentPlayer?.hasGuessed ?? false;
    const tentativeGuess = currentPlayer?.tentativeGuess;
    const hasTentativeGuess = !!(tentativeGuess && tentativeGuess.mapId && tentativeGuess.mapId === currentMap.id);

    // Check if the submitted guess is on the current map
    const isSubmittedGuessOnCurrentMap = submittedGuess && submittedGuess.mapId === currentMap.id;

    // Use submitted guess if available and on current map, otherwise tentative guess, otherwise local guess position
    const guessPosition = isSubmittedGuessOnCurrentMap
        ? { lat: submittedGuess.x, lng: submittedGuess.y }
        : hasTentativeGuess && tentativeGuess
        ? { lat: tentativeGuess.x, lng: tentativeGuess.y }
        : localGuessPosition;

    // Convert real coordinates back to leaflet coordinates for display
    const getLeafletCoords = useCallback((realX: number, realY: number) => {
        if (!currentMap.size) return null;
        const leafletCoords = gameToLeafletCoordinates(realX, realY, currentMap);
        return [leafletCoords.lat, leafletCoords.lng] as [number, number];
    }, [currentMap]);

    // Convert guess position to leaflet coordinates for the control
    const guessMarkerPosition = useMemo(() => {
        if (!guessPosition || !currentMap.size) return null;
        // guessPosition.lat contains x coordinate, guessPosition.lng contains y coordinate
        const leafletCoords = getLeafletCoords(guessPosition.lat, guessPosition.lng);
        if (!leafletCoords) return null;
        return { lat: leafletCoords[0], lng: leafletCoords[1] };
    }, [guessPosition, currentMap.size, getLeafletCoords]);

    const setLocalGuessPosition = useCallback((position: { lat: number, lng: number } | null) => {
        // Only allow setting local guess if no guess has been submitted and not disabled
        if (!hasSubmittedGuess && !disabled) {
            setLocalGuessPositionState(position);
        }
    }, [hasSubmittedGuess, disabled]);

    const placeGuess = useCallback((position: { lat: number, lng: number }) => {
        if (!hasSubmittedGuess && room) {
            room.send("placeGuess", {
                mapId: currentMap.id,
                x: position.lat,
                y: position.lng
            });
            // Also update local state for immediate visual feedback
            setLocalGuessPositionState(position);
        }
    }, [hasSubmittedGuess, room, currentMap.id]);

    const confirmGuess = useCallback(() => {
        if (!hasSubmittedGuess && room && (hasTentativeGuess || localGuessPosition)) {
            room.send("confirmGuess");
            // Clear local state as it will be replaced by server state
            setLocalGuessPositionState(null);
        }
    }, [hasSubmittedGuess, room, hasTentativeGuess, localGuessPosition]);

    // Reset local guess when disabled changes (new round)
    useEffect(() => {
        if (disabled) {
            setLocalGuessPositionState(null);
        }
    }, [disabled]);

    // Clear local guess when switching maps if no guess has been submitted
    useEffect(() => {
        if (!hasSubmittedGuess) {
            setLocalGuessPositionState(null);
        }
    }, [currentMap.id, hasSubmittedGuess]);

    return (
        <GuessContext.Provider value={{
            localGuessPosition,
            setLocalGuessPosition,
            placeGuess,
            confirmGuess,
            hasSubmittedGuess,
            hasTentativeGuess,
            guessMarkerPosition,
        }}>
            {children}
        </GuessContext.Provider>
    );
}

export function useGuess(): GuessContext {
    return useContext(GuessContext);
}